import {
    BaseChart,
    ChartMouseHandler,
    EChartsOption,
    getConfiguredInstance,
    useChart,
} from '@/components/ECharts';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { jsxToHtml } from '@/utils/reactUtils';
import { Typography } from '@analytical-alley/ui';
import { BarSeriesOption } from 'echarts';
import EChartsReact from 'echarts-for-react';
import React, {
    Ref,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

interface GraphProps {
    chartRef?: Ref<EChartsReact | null>;
    selected?: string;
    onSelected?: (value: string) => void;
    getChartOptions?: (chartOptionSeries: ChartOptions['series']) => void;
}

type AreaTooltipProps = {
    seriesName: string;
    color: string;
    value: number;
    axisValueLabel: string;
}[];
type ChartOptions = EChartsOption<BarSeriesOption>;

const AreaTooltip = ({
    params,
    selectedSeriesName,
    formatNumber,
}: {
    params: AreaTooltipProps;
    selectedSeriesName?: string;
    formatNumber: (val: number) => string;
}) => {
    if (!selectedSeriesName) {
        return null;
    }

    const hoveredItem = params.find(
        (param) => param.seriesName === selectedSeriesName,
    );

    if (!hoveredItem) {
        return null;
    }

    return (
        <div className="tooltip-content">
            <Typography className="text-violet-950 dark:text-violet-950 font-medium font-sans">
                {params[0]?.axisValueLabel} effect on your KPI
            </Typography>
            <Typography className="text-violet-950 dark:text-violet-950 font-medium">
                {formatNumber(hoveredItem.value)}
            </Typography>
        </div>
    );
};

const getGraphData = ({
    seriesData,
    xAxisData,
    formatNumber,
}: {
    xAxisData: string[];
    seriesData: {
        variable: string;
        category: string;
        color: string;
        value: number | undefined;
    }[];
    formatNumber: (value: number) => string;
    period: string | undefined;
}): ChartOptions => {
    const totalInvestmentSum = seriesData.reduce(
        (acc, obj) => acc + Math.abs(obj.value!),
        0,
    );
    const series: ChartOptions['series'] = [
        {
            name: 'Competitor',
            type: 'bar',
            stack: 'Total',
            color: '',
            barMaxWidth: 200,
            data: seriesData.map(({ variable, value, color }) => ({
                name: variable,
                value,
                label: {
                    show: true,
                    position: value && value > 0 ? 'right' : 'left',
                    color: '#fff',
                    formatter: () =>
                        `${Math.round((value! / totalInvestmentSum) * 100)}%`,
                },
                itemStyle: {
                    color,
                    borderRadius:
                        value && value > 0 ? [0, 5, 5, 0] : [5, 0, 0, 5],
                },
            })),
        },
    ];

    return {
        animation: true,
        tooltip: {
            show: false,
            trigger: 'axis',
            triggerOn: 'mousemove|click',
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 0,
            type: 'item',
            confine: true,
            formatter: '',
        },
        legend: {
            show: false,
        },
        grid: { right: '2%', left: '10%' },
        xAxis: [
            {
                type: 'value',
                position: 'bottom',
                boundaryGap: ['0%', '0%'],
                show: true,
                axisLine: {
                    show: true,
                    lineStyle: {
                        opacity: 0.5,
                    },
                },
                axisLabel: {
                    formatter: (value: number) => formatNumber(Number(value)),
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'solid',
                        opacity: 0.5,
                    },
                },
            },
        ],
        yAxis: [
            {
                type: 'category',
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                data: xAxisData,
            },
        ],
        series: series,
    };
};

export const CompetitorMediaEffectOnClientRevenueGraph = memo(
    ({ selected }: GraphProps) => {
        const { competitorsMediaEffectData, period } =
            useCompetitorsMediaInvestmentContext();
        const { contributionFormatter } = useDashboardGlobalContext();

        const { variableData, totalInvestmentByVariable } =
            competitorsMediaEffectData;

        const eChartsOptions = useMemo(() => {
            return getGraphData({
                seriesData: variableData.map((obj) => ({
                    ...obj,
                    value: totalInvestmentByVariable.get(obj.variable),
                })),
                xAxisData: variableData.map((obj) => obj.variable),
                period: period,
                formatNumber: contributionFormatter,
            });
        }, [
            period,
            totalInvestmentByVariable,
            variableData,
            contributionFormatter,
        ]);

        const [hovered, setHovered] = useState('');

        const selectedRef = useRef(selected);
        const timeoutRef = useRef<number | null>(null);

        useEffect(() => {
            selectedRef.current = selected;
        }, [selected]);

        const onMouseOver: ChartMouseHandler = useCallback((context) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (context.seriesName) {
                setHovered(
                    selectedRef.current
                        ? selectedRef.current
                        : context.seriesName,
                );
            }
        }, []);

        const onMouseOut: ChartMouseHandler = useCallback(() => {
            if (selectedRef.current) {
                return;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                setHovered('');
            }, 0);
        }, []);

        const { ref, chartOptions, onEvents, isChartReady, onChartReady } =
            useChart({
                chartOptions: eChartsOptions,
                onMouseOver,
                onMouseOut,
            });

        useEffect(() => {
            // Guarded: until the option has landed, `BaseChart` renders with
            // `notMerge`, so anything set here would be discarded.
            const instance = getConfiguredInstance(ref.current);

            if (instance) {
                instance.setOption({
                    tooltip: {
                        showDelay: selected ? 0 : 50,
                        show: true,
                        formatter: (params: AreaTooltipProps) => {
                            return jsxToHtml(
                                <AreaTooltip
                                    params={params}
                                    selectedSeriesName={hovered}
                                    formatNumber={contributionFormatter}
                                />,
                            );
                        },
                    },
                });
            }
        }, [ref, hovered, selected, contributionFormatter, isChartReady]);

        return (
            <div className="relative w-full h-[47.5rem]">
                <BaseChart
                    $shouldExpand
                    option={chartOptions}
                    ref={ref}
                    onEvents={onEvents}
                    onChartReady={onChartReady}
                />
            </div>
        );
    },
);

CompetitorMediaEffectOnClientRevenueGraph.displayName =
    'CompetitorMediaEffectOnClientRevenueGraph';
